import { useState } from "react";
import { Link } from "react-router-dom";
import { Users, Dumbbell, Calendar, MessageSquare } from "lucide-react";
import RichText from "@/lib/richText";
import UserLink from "@/components/UserLink";
import TitleTag from "@/components/TitleTag";
import SportIcon from "@/components/SportIcon";
import PostMedia from "@/components/PostMedia";
import MediaEditor from "@/components/MediaEditor";
import PollView from "@/components/PollView";
import PostActions from "@/components/PostActions";
import AuthorThankTip from "@/components/AuthorThankTip";
import FlagButton from "@/components/FlagButton";
import { CornerDownRight, Trash2, Pin, Pencil, Check, X } from "lucide-react";
import { ReactionBar, AuthorAvatar, timeAgo } from "@/components/BoardReplies";

// Left color bar + header icon per source type.
const TYPE = {
    team: { color: "#ff4500", icon: Users },
    trainer: { color: "#2196f3", icon: Dumbbell },
    trainer_listing: { color: "#2196f3", icon: Dumbbell },
    event: { color: "#22c55e", icon: Calendar },
    wall: { color: "#a855f7", icon: MessageSquare },
    forum: { color: "#c19a6b", icon: MessageSquare },
};

function hasDistinctSubject(p) {
    const subj = (p.subject || "").trim();
    if (!subj) return false;
    const firstLine = (p.body || "").split("\n")[0].trim().toLowerCase();
    const s = subj.toLowerCase();
    return firstLine !== s && !firstLine.startsWith(s);
}

/**
 * A full, functional feed post (photos, reactions, repost/follow, thank/tip, reply)
 * in the same format as the message board, with a left color bar and a listing
 * header row (icon + title) on top. Used by "From who you follow" and "Trending".
 */
export default function FeedPostCard({ item, accentDefault = "#ff4500", onDelete, onEdit, onPin }) {
    const entity = item.entity || {};
    const t = TYPE[entity.type] || TYPE.wall;
    const color = t.color;
    const Icon = t.icon;
    const isWall = entity.type === "wall";
    const postType = item.post_type || item._src || "board";
    const thanks = item.author_thanks_count || 0;
    const postLink = `${entity.link || "/"}#post-${item.id}`;
    const flagType = postType === "wall" ? "post" : "post";
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(item.body || "");
    const [media, setMedia] = useState({ images: item.images || [], videos: item.videos || [], gif: item.gif || null });
    const [saving, setSaving] = useState(false);

    const startEdit = () => {
        setDraft(item.body || "");
        setMedia({ images: item.images || [], videos: item.videos || [], gif: item.gif || null });
        setEditing(true);
    };
    const hasMedia = media.images.length || media.videos.length || media.gif;
    const saveEdit = async () => {
        setSaving(true);
        try { await onEdit(item, draft, media); setEditing(false); }
        finally { setSaving(false); }
    };

    return (
        <div className="border border-[#2a2a2a] bg-[#141414]" style={{ borderLeft: `4px solid ${color}` }} data-testid={`feed-post-${item.id}`}>
            <div className="p-4">
                {item.pinned && (
                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-2" data-testid={`feed-post-pinned-${item.id}`}>
                        <Pin className="h-3 w-3 fill-current" /> Pinned
                    </div>
                )}
                {/* Header row: listing icon + title */}
                <Link to={entity.link || "/"} className="group flex items-center gap-2 min-w-0" data-testid={`feed-post-listing-${item.id}`}>
                    {!isWall && entity.sport
                        ? <SportIcon sport={entity.sport} variant="line" size={18} className="text-[#a0a0a0] shrink-0" />
                        : <Icon className="h-4 w-4 shrink-0" style={{ color }} />}
                    <span className="font-display tracking-wide text-white truncate group-hover:text-[#ff4500]">
                        {isWall ? (entity.name || "Wall post") : (entity.name || "Listing")}
                    </span>
                </Link>

                {/* Author row: avatar + username + role + thank/tip/chevron + time */}
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] mt-2 flex items-center gap-1.5">
                    <UserLink username={item.author_username}><AuthorAvatar photo={item.author_photo} name={item.author_username} count={thanks} size={18} /></UserLink>
                    <span><UserLink username={item.author_username} /></span>
                    <TitleTag title={item.author_title} isPro={item.author_is_pro} />
                    {item.author_id && <AuthorThankTip userId={item.author_id} username={item.author_username} tipLinks={item.author_tip_links} thanksCount={thanks} contextType="post" contextId={item.id} />}
                    <span className="ml-auto whitespace-nowrap">{item.edited_at ? <span title={new Date(item.edited_at).toLocaleString()}>edited · </span> : ""}{timeAgo(item.created_at)}</span>
                </div>

                {/* Body (inline-editable when onEdit is provided) */}
                {editing ? (
                    <div className="mt-2" data-testid={`feed-post-edit-${item.id}`}>
                        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} data-testid={`feed-post-edit-input-${item.id}`}
                            className="w-full bg-[#0e0e0e] border border-[#262626] text-[#eaeaea] text-sm px-3 py-2 rounded focus:outline-none focus:border-[#ff4500]" />
                        <MediaEditor value={media} onChange={setMedia} accent={color} testidPrefix={`feed-post-media-${item.id}`} />
                        <div className="flex items-center gap-2 mt-2">
                            <button onClick={saveEdit} disabled={saving || (!draft.trim() && !hasMedia)} data-testid={`feed-post-edit-save-${item.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] border border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e]/10 disabled:opacity-40"><Check className="h-3 w-3" /> Save</button>
                            <button onClick={() => { setDraft(item.body || ""); setEditing(false); }} data-testid={`feed-post-edit-cancel-${item.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] border border-[#2a2a2a] text-[#a0a0a0] hover:text-white"><X className="h-3 w-3" /> Cancel</button>
                        </div>
                    </div>
                ) : (
                    <>
                        {hasDistinctSubject(item) && <div className="font-display font-bold text-lg tracking-wide mt-2">{item.subject}</div>}
                        {item.body && <p className="text-sm text-[#cfcfcf] mt-2"><RichText text={item.body} /></p>}
                    </>
                )}
                {!editing && <PostMedia images={item.images} gif={item.gif} videos={item.videos} />}
                <PollView postType={postType} postId={item.id} poll={item.poll} accent={color} />

                {/* Action row */}
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <Link to={postLink} data-testid={`feed-post-reply-${item.id}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] hover:opacity-80 shrink-0" style={{ color }}>
                        <CornerDownRight className="h-3 w-3" /> {item.reply_count || 0} {item.reply_count === 1 ? "reply" : "replies"}
                    </Link>
                    <PostActions
                        dense
                        postType={postType}
                        postId={item.id}
                        link={postLink}
                        accent={color}
                        shareText={item.subject || item.body}
                        leading={<ReactionBar post={item} accent={color} />}
                        initial={{ i_reposted: item.i_reposted, repost_count: item.repost_count, i_follow: item.i_follow, follow_count: item.follow_count }}
                    />
                    <FlagButton targetType={flagType} targetId={item.id} testid={`feed-post-flag-${item.id}`} />
                    <div className="ml-auto flex items-center gap-2">
                        {onPin && (
                            <button type="button" onClick={() => onPin(item)} data-testid={`feed-post-pin-${item.id}`}
                                className={`transition-colors ${item.pinned ? "text-[#ff4500]" : "text-[#a0a0a0] hover:text-[#ff4500]"}`} title={item.pinned ? "Unpin from top" : "Pin to top of your wall"}>
                                <Pin className={`h-4 w-4 ${item.pinned ? "fill-current" : ""}`} />
                            </button>
                        )}
                        {onEdit && !editing && (
                            <button type="button" onClick={startEdit} data-testid={`feed-post-edit-btn-${item.id}`}
                                className="text-[#a0a0a0] hover:text-[#ff4500] transition-colors" title="Edit post">
                                <Pencil className="h-4 w-4" />
                            </button>
                        )}
                        {onDelete && (
                            <button type="button" onClick={() => onDelete(item)} data-testid={`feed-post-delete-${item.id}`}
                                className="text-[#a0a0a0] hover:text-red-400 transition-colors" title="Delete post">
                                <Trash2 className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
